import {
  getAnimalScientificName,
  getAnimalSearchLabel,
  isAcceptedAnimalTaxon,
  normalizeAnimalKeyword,
} from '@/lib/animalKeywordLabels';

const CACHE_PREFIX = 'inaturalist@animal-photo:';
const CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 7;
const API_BASE = 'https://api.inaturalist.org/v1';

const memoryCache = new Map();
const inflightRequests = new Map();

function readCache(keyword) {
  const normalized = normalizeAnimalKeyword(keyword);
  if (!normalized) {
    return null;
  }

  if (memoryCache.has(normalized)) {
    return memoryCache.get(normalized);
  }

  try {
    const raw = sessionStorage.getItem(`${CACHE_PREFIX}${normalized}`);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed?.url || !parsed?.cachedAt) {
      return null;
    }

    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) {
      sessionStorage.removeItem(`${CACHE_PREFIX}${normalized}`);
      return null;
    }

    memoryCache.set(normalized, parsed);
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(keyword, payload) {
  const normalized = normalizeAnimalKeyword(keyword);
  if (!normalized || !payload?.url) {
    return;
  }

  const entry = {
    ...payload,
    cachedAt: Date.now(),
  };

  memoryCache.set(normalized, entry);

  try {
    sessionStorage.setItem(`${CACHE_PREFIX}${normalized}`, JSON.stringify(entry));
  } catch {
    // Ignora quota excedida; memória ainda cobre a sessão atual.
  }
}

function pickPhotoFromTaxa(results) {
  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }

  const taxon = results.find(isAcceptedAnimalTaxon) || results.find((item) => item?.default_photo?.square_url);
  if (!taxon?.default_photo?.square_url) {
    return null;
  }

  return {
    url: taxon.default_photo.square_url,
    mediumUrl: taxon.default_photo.medium_url || taxon.default_photo.square_url,
    attribution: taxon.default_photo.attribution || '',
    commonName: taxon.preferred_common_name || taxon.matched_term || '',
    scientificName: taxon.name || '',
  };
}

async function fetchTaxaResults(query, useLocale) {
  const params = new URLSearchParams({
    q: query,
    per_page: '5',
    is_active: 'true',
  });

  if (useLocale) {
    params.set('locale', 'pt-BR');
  }

  const endpoint = useLocale ? `${API_BASE}/taxa/autocomplete` : `${API_BASE}/taxa`;
  const response = await fetch(`${endpoint}?${params.toString()}`);

  if (!response.ok) {
    return [];
  }

  const data = await response.json();
  return data?.results || [];
}

async function resolveAnimalPhoto(keyword) {
  const cached = readCache(keyword);
  if (cached) {
    return cached;
  }

  const label = getAnimalSearchLabel(keyword);
  let photo = pickPhotoFromTaxa(await fetchTaxaResults(label, true));

  if (!photo) {
    const scientificName = getAnimalScientificName(keyword);
    if (scientificName) {
      photo = pickPhotoFromTaxa(await fetchTaxaResults(scientificName, false));
    }
  }

  if (!photo) {
    return null;
  }

  writeCache(keyword, photo);
  return photo;
}

export async function fetchAnimalPhotoByKeyword(keyword) {
  const normalized = normalizeAnimalKeyword(keyword);
  if (!normalized) {
    return null;
  }

  if (inflightRequests.has(normalized)) {
    return inflightRequests.get(normalized);
  }

  const promise = resolveAnimalPhoto(normalized).finally(() => {
    inflightRequests.delete(normalized);
  });

  inflightRequests.set(normalized, promise);
  return promise;
}

export function getCachedAnimalPhoto(keyword) {
  return readCache(keyword);
}
